import { useState } from 'react'
import { FolderOpen } from 'lucide-react'

import { PageShell } from '@/components/common/PageShell'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Card } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { useMe } from '@/features/settings/hooks/useMe'
import { useMyCompanies } from '@/features/companies/hooks/useCompanies'
import { usePermissions } from '@/hooks/usePermissions'
import { StaffDocumentsTab } from './StaffDocumentsTab'

export function MyDocumentsPage(): React.ReactElement {
  const { can } = usePermissions()
  const { data: me, isLoading: meLoading } = useMe()
  const { data: myCompanies = [] } = useMyCompanies()
  const [companyId, setCompanyId] = useState<string | null>(null)

  const meId = me?._id ?? ''
  const selectedSet = companyId ? new Set([companyId]) : new Set<string>()
  const canReadOwn = can('documents', 'read_self')
  const canCreate = can('documents', 'create_self')
  const canUpdate = can('documents', 'update_self')
  const canDelete = can('documents', 'delete_self')

  // Single-active semantics: clicking a selected chip clears it (back to "All companies").
  // Clicking another chip switches selection.
  const onToggle = (id: string | null): void => {
    if (id === null) {
      setCompanyId(null)
      return
    }
    setCompanyId((prev) => (prev === id ? null : id))
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Documents' }]}
      icon={FolderOpen}
      title="My Documents"
      description="Documents uploaded by or for you, across every company you belong to."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <CompanyChips
              companies={myCompanies}
              selectedCompanyIds={selectedSet}
              onToggle={onToggle}
            />
          }
        />

        {meLoading || !meId ? (
          <Card>
            <SectionLoader variant="list" label="Loading documents" />
          </Card>
        ) : (
          <StaffDocumentsTab
            meId={meId}
            companyId={companyId}
            canReadOwn={canReadOwn}
            canReadOthers={false}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            canManageOthers={false}
          />
        )}
      </div>
    </PageShell>
  )
}

MyDocumentsPage.displayName = 'MyDocumentsPage'
